import {Fragment, useEffect, useState, type FC} from "react"
import {
  Menu as MUIMenu,
  Typography,
  Divider,
  Grid,
  Chip,
  IconButton,
  Box,
} from "@mui/material"
import {
  DeliverableModel,
  RequestObject,
  REQUEST_STATUS,
  RequestsQueueStore,
  // REQUEST_TYPES,
} from "../../../types"
import {StoreApi, UseBoundStore} from "zustand"
import {RequestsQueueFunctions} from "../../../services"
import {Tooltip} from "../atoms/tooltip/Tooltip"
import RequestsQueueProgress from "./RequestsQueueProgress"
import {getDeliverableExtension, iconMap} from "../../../helpers"
import {CloseIcon} from "../../../assets/icons"
import {ReactSVG} from "react-svg"
import {useTranslation} from "react-i18next"

type MenuProps = {
  useRequestsQueueStore: UseBoundStore<StoreApi<RequestsQueueStore>>
}

type RequestItemProps = {
  onClearSingle: (index: number) => void
  item: RequestObject
  index: number
  isClearable: boolean
}

type ClearAllButtonProps = {
  onClearAll: () => void
  disabled: boolean
}

const ClearAllButton: FC<ClearAllButtonProps> = (props) => {
  const {onClearAll, disabled} = props
  const {t} = useTranslation()

  const style = disabled
    ? {
        color: "#aca8a8",
        fontSize: "14px",
        lineHeight: "1.5rem",
        fontWeight: 400,
        cursor: "default",
      }
    : {
        color: "#226ca6",
        fontSize: "14px",
        lineHeight: "1.5rem",
        textDecoration: "underline",
        fontWeight: 400,
        cursor: "pointer",
      }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "end",
        alignItems: "center",
      }}
    >
      {disabled ? (
        <Tooltip
          title={t("Cannot clear while in progress")}
          arrow
          placement="bottom"
        >
          <Typography sx={style}>{t("Clear")}</Typography>
        </Tooltip>
      ) : (
        <Typography
          onClick={() => {
            onClearAll()
          }}
          sx={style}
        >
          {t("Clear")}
        </Typography>
      )}

      <IconButton
        disabled={disabled}
        onClick={() => {
          onClearAll()
        }}
        sx={{
          backgroundColor: "unset",
          "&:hover": {
            backgroundColor: "unset",
            boxShadow: "unset",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}
const RequestItemStatus = (objectStatus: REQUEST_STATUS) => {
  if (
    objectStatus == REQUEST_STATUS.QUEUED ||
    objectStatus == REQUEST_STATUS.INPROGRESS ||
    objectStatus == REQUEST_STATUS.SIGNALR
  ) {
    return {
      label: "In Progress",
      style: {
        backgroundColor: "#d3e2ed",
        fontSize: "14px",
        fontFamily: "Open Sans",
        color: "#003a67",
        "&.MuiChip-root": {
          borderRadius: "4px",
        },
      },
    }
  }

  if (objectStatus == REQUEST_STATUS.COMPLETED) {
    return {
      label: "Successful",
      style: {
        backgroundColor: "#deedd6",
        fontSize: "14px",
        fontFamily: "Open Sans",
        color: "#164600",
        "&.MuiChip-root": {
          borderRadius: "4px",
        },
      },
    }
  }

  if (objectStatus == REQUEST_STATUS.FAILED) {
    return {
      label: "Failed",
      style: {
        backgroundColor: "#f6d5d2",
        fontSize: "14px",
        fontFamily: "Open Sans",
        color: "#300000",
        "&.MuiChip-root": {
          borderRadius: "4px",
        },
      },
    }
  }
}

const RequestItem: FC<RequestItemProps> = (props) => {
  const {index, item, onClearSingle, isClearable} = props
  const {t} = useTranslation()
  return (
    <Fragment>
      <Box
        key={index}
        sx={{
          padding: "8px",
          paddingLeft: "16px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "start",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              minWidth: "28px",
              svg: {
                width: "24px",
                height: "24px",
                fill: "#242D35",
              },
            }}
          >
            <ReactSVG
              src={`/icons${
                iconMap[
                  getDeliverableExtension(
                    item.requestType.source?.toLowerCase() || "",
                  )
                ]
              }.svg`}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "1.5rem",
              fontWeight: 400,
            }}
          >
            {item.displayName}
          </Typography>
        </Box>
        <Box>
          {item.state == REQUEST_STATUS.FAILED ? (
            <Tooltip
              title={t("Action failed. Try again")}
              arrow
              placement="bottom"
            >
              <Chip
                label={t(RequestItemStatus(item.state)?.label || "")}
                sx={RequestItemStatus(item.state)?.style}
              />
            </Tooltip>
          ) : (
            <Chip
              label={t(RequestItemStatus(item.state)?.label || "")}
              sx={RequestItemStatus(item.state)?.style}
            />
          )}

          <IconButton
            disabled={!isClearable}
            onClick={() => {
              onClearSingle(index)
            }}
            sx={{
              backgroundColor: "unset",
              "&:hover": {
                backgroundColor: "unset",
                boxShadow: "unset",
              },
            }}
          >
            {isClearable ? <CloseIcon /> : <Box width={"24px"} />}
          </IconButton>
        </Box>
      </Box>
    </Fragment>
  )
}

const RequestsQueueMenu: FC<MenuProps> = (props) => {
  const {useRequestsQueueStore} = props
  const {t} = useTranslation()
  const {executeRequest} = RequestsQueueFunctions(t)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const [triggerQueueUpdate, setTriggerQueueUpdate] = useState<{
    isUpdate: boolean
    item: DeliverableModel | null
    index: number | null
    success: boolean | undefined
  }>({isUpdate: false, item: null, index: null, success: undefined})

  const requestsQueue = useRequestsQueueStore((state) => state.queuedRequests)
  const completedRequests = useRequestsQueueStore(
    (state) => state.completedRequests,
  )
  const isBusy = useRequestsQueueStore((state) => state.isBusy)
  const setIsBusy = useRequestsQueueStore((state) => state.setIsBusy)
  const setUpdateQueue = useRequestsQueueStore((state) => state.setUpdateQueue)
  const setUpdateCompletedRequests = useRequestsQueueStore(
    (state) => state.setUpdateCompletedRequests,
  )

  //clears the completed items only
  const onClearAll = () => {
    setUpdateCompletedRequests([])
  }

  //clears a single completed item
  const onClearSingle = (index: number) => {
    const newArray = completedRequests.slice(0)
    newArray.splice(index, 1)

    setUpdateCompletedRequests([...newArray])
  }

  //shows browser alert if there are still any requests in progress
  useEffect(() => {
    if (requestsQueue.length > 0) {
      window.onbeforeunload = function () {
        return true
      }
    }

    return () => {
      window.onbeforeunload = null
    }
  }, [requestsQueue])

  //updates the queue based on the result of the request
  useEffect(() => {
    if (!triggerQueueUpdate.isUpdate) return

    //if the request object state was already modified we dont need to update the queue
    if (requestsQueue[0].state !== REQUEST_STATUS.INPROGRESS) return
    if (requestsQueue.length == 0) return

    //if request was error
    if (triggerQueueUpdate.success == false) {
      setIsBusy(false)

      //if request fails, update the request object to be status failed
      const newQueue = requestsQueue.slice(1)
      requestsQueue[0].state = REQUEST_STATUS.FAILED

      setUpdateQueue([...newQueue])
      setUpdateCompletedRequests([...completedRequests, requestsQueue[0]])

      setTriggerQueueUpdate({
        isUpdate: false,
        item: null,
        index: null,
        success: undefined,
      })
      return
    }

    requestsQueue[0].state = REQUEST_STATUS.SIGNALR

    let updateQueueItem: DeliverableModel | null = triggerQueueUpdate.item

    requestsQueue[0].id = updateQueueItem?.id || null
    setUpdateQueue([...requestsQueue])

    setTriggerQueueUpdate({
      isUpdate: false,
      item: null,
      index: null,
      success: undefined,
    })
  }, [triggerQueueUpdate])

  //executes the request in the queue in order
  useEffect(() => {
    if (requestsQueue.length == 0) return
    if (requestsQueue[0].state !== REQUEST_STATUS.QUEUED) return
    if (isBusy) return

    setIsBusy(true)
    const newQueue = requestsQueue.slice(0)
    newQueue[0].state = REQUEST_STATUS.INPROGRESS
    setUpdateQueue([...newQueue])

    //function that executes the request item
    executeRequest(requestsQueue[0], setTriggerQueueUpdate)
  }, [requestsQueue, isBusy])

  return (
    <Fragment>
      <IconButton
        onClick={handleOpen}
        sx={{
          backgroundColor: "unset",
          "&:hover": {
            backgroundColor: "unset",
            boxShadow: "unset",
          },
        }}
      >
        <RequestsQueueProgress
          isInProgress={requestsQueue.length > 0}
          isFailed={
            completedRequests?.filter((el) => el.state == REQUEST_STATUS.FAILED)
              .length > 0
          }
          isSuccess={
            completedRequests?.filter(
              (el) => el.state == REQUEST_STATUS.COMPLETED,
            ).length > 0
          }
        />
      </IconButton>
      <MUIMenu
        transformOrigin={{horizontal: "right", vertical: "top"}}
        anchorOrigin={{horizontal: "right", vertical: "bottom"}}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          mt: "10px",
          "& .MuiMenu-paper": {
            minWidth: "400px",
            backgroundColor: "#FFFFFF",
            borderRadius: "0.25rem",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "#DFDFDF",
          },
          "& .MuiMenu-list": {
            padding: "0",
          },
          "& .MuiMenuItem-root": {
            "&:hover": {
              backgroundColor: "#FFFFFF",
            },
          },
        }}
      >
        <Grid
          container
          direction="row"
          sx={{
            padding: "8px",
            paddingLeft: "16px",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "1.5rem",
                fontWeight: 600,
              }}
            >
              {t("Progress status")}
            </Typography>
          </Grid>
          <Grid>
            <ClearAllButton
              disabled={completedRequests.length == 0}
              onClearAll={onClearAll}
            />
          </Grid>
        </Grid>
        <Divider />
        {completedRequests.map((item, index) => (
          <RequestItem
            key={index}
            isClearable={true}
            index={index}
            onClearSingle={onClearSingle}
            item={item}
          />
        ))}
        {requestsQueue.map((item, index) => (
          <RequestItem
            key={index}
            isClearable={false}
            index={index}
            onClearSingle={onClearSingle}
            item={item}
          />
        ))}
      </MUIMenu>
    </Fragment>
  )
}

export default RequestsQueueMenu
